<template>
  <div class="car-model-info" :class="{ 'shake-animation': isShaking }">
    <div v-if="errorMessage" class="error-message top-error">
      {{ errorMessage }}
    </div>
    <!-- 骨架屏展示 -->
    <SkeletonLoader v-if="loading" />
    <!-- 数据展示 -->
    <div class="info-list" v-else-if="data">
      <div
        v-for="(item, index) in filteredInfoItems"
        :key="index"
        class="info-item"
        :class="{ 'full-width': item.fullWidth }"
      >
        <span class="label">{{ item.label }}</span>
        <template v-if="item.type === 'image'">
          <div class="image-wrapper">
            <el-image
              v-if="formattedData[item.key]"
              :src="formattedData[item.key]"
              fit="contain"
              :preview-src-list="formattedData.carModelPics"
            />
            <span v-else>--</span>
          </div>
        </template>
        <span v-else class="value">{{ formattedData[item.key] }}</span>
      </div>
    </div>
    <!-- 空状态提示 -->
    <EmptyState v-else />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { OPERATION_TYPE_ENUM } from '../../../config/list'
import { judgePureElectricDrivingRange, judgeFuelConsumption } from './config'
import SkeletonLoader from './SkeletonLoader.vue'
import EmptyState from './EmptyState.vue'
import { useCarModelValidation } from './useCarModelValidation'

const props = defineProps({
  data: {
    type: Object,
    default: () => null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const isShaking = ref(false)
const errorMessage = ref('')

// 使用提取的验证 hook
const { validateModelData, clearError, shake } = useCarModelValidation({
  errorMessage,
  isShaking,
  judgePureElectricDrivingRange,
  judgeFuelConsumption,
  OPERATION_TYPE_ENUM,
})

// 车型等级查询结果标识  0：未查询到，1：查询到
const CAR_LEVEL_FLAG_ENUM = {
  NOT_QUERY: 0,
  QUERY: 1,
}

// 显示 纯电续航里程： 能源类型为 纯电动、插电式混动、增程式混动、油电混合
const showPureElectricDrivingRange = computed(() => {
  const { energyType } = props.data
  return judgePureElectricDrivingRange(energyType)
})

// 显示 综合油耗： 能源类型为 汽油、插电式混动、增程式混动、油电混合、柴油、天然气
const showFuelConsumption = computed(() => {
  const { energyType } = props.data
  return judgeFuelConsumption(energyType)
})

const showCarLevelName = computed(() => {
  const { operationType } = props.data
  if (operationType !== OPERATION_TYPE_ENUM.NETWORK) return false
  return true
})

// 定义信息项配置
const infoItems = computed(() => [
  { label: '车型年款：', key: 'modelYear' },
  {
    label: '纯电续航里程：',
    key: 'pureElectricDrivingRange',
    show: showPureElectricDrivingRange.value,
  },
  {
    label: '综合油耗：',
    key: 'fuelConsumption',
    show: showFuelConsumption.value,
  },
  { label: '运营类型：', key: 'operationTypeName' },
  {
    label: '网约车接单资质：',
    key: 'carLevelName',
    show: showCarLevelName.value,
  },
  {
    label: '车型图片：',
    key: 'carModelPic',
    type: 'image',
    fullWidth: true,
  },
])

// 添加过滤后的信息项计算属性
const filteredInfoItems = computed(() => {
  return infoItems.value.filter((item) => item.show !== false)
})

const formattedData = computed(() => {
  const { data } = props
  const { carLevelFlag, carLevelName } = data
  return {
    ...data,
    carModelPic: data?.carModelPics?.[0] || '', // 车型图片：必填项
    modelYear: formatValue(data?.modelYear, '款'), // 车型年款：必填项。示例：2022款
    pureElectricDrivingRange: formatValue(data?.pureElectricDrivingRange, 'km'),
    fuelConsumption: formatValue(data?.fuelConsumption, 'L/100km'),
    // 运营类型：下拉菜单，回显已填写的对应信息。枚举值有：网约车、物流车
    carLevelName:
      CAR_LEVEL_FLAG_ENUM.QUERY === carLevelFlag && carLevelName
        ? carLevelName
        : '该车型暂无可接单类型，请与合作网约平台进行确认',
  }
})

// 格式化显示值
const formatValue = (value, unit = '') => {
  if (!value && value !== 0) return '--'
  return unit ? `${value}${unit}` : value
}

// 监听数据变化，自动验证
watch(
  () => props.data,
  (n, o) => {
    if (n === o) return
    validateModelData(props.data, props.loading)
  },
  { deep: true },
)

// 组件挂载时验证
onMounted(() => {
  validateModelData(props.data, props.loading)
})

const validate = () => {
  return validateModelData(props.data, props.loading)
}

// 暴露方法给父组件
defineExpose({
  shake,
  clearError,
  validate,
})
</script>

<style lang="scss" scoped>
@import '@/styles/transition.scss';

.car-model-info {
  padding: 12px;
  margin: 0 0 24px 100px;
  background: #f3f3f4;
  border-radius: 8px;
  opacity: 1;
  min-height: 200px;
  background: #f3f3f4;
  margin-top: -16px;

  /* 错误信息样式 */
  .error-message {
    color: #f56c6c;
    font-size: 12px;
    line-height: 1;
    padding: 4px 0;
  }
  .top-error {
    margin-bottom: 8px;
  }

  .bottom-error {
    margin-top: 8px;
  }

  .info-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .info-item {
      display: flex;
      align-items: flex-start;

      &.full-width {
        width: 100%;
      }

      .label {
        width: auto;
        color: #606266;
        margin-right: 4px;
        flex-shrink: 0;
        text-align: right;
      }

      .value {
        color: #303133;
        flex: 1;
      }

      .image-wrapper {
        width: 120px;
        overflow: hidden;
      }
    }
  }

  .edit-btn {
    color: #366cff;
    font-size: 14px;
    cursor: pointer;
    margin-top: 16px;

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
./useCarModelValidation
